/**
 * Who is signed in.
 *
 * This replaces the localStorage identity label the tool used to have. The difference is not
 * cosmetic: that label was a *claim* the client made about itself and the server believed —
 * `annotator_id` travelled in every write body — so a box's author was whatever the browser
 * said. Now the browser holds a bearer token, the server derives the annotator id from it,
 * and there is no field a client could use to write under somebody else's name.
 *
 * What is kept, and where
 * -----------------------
 * The token lives in `localStorage` so a reload does not sign you out. That is a deliberate
 * trade with a real cost — a token in `localStorage` is readable by any script that gets onto
 * the page, where a `httpOnly` cookie would not be. It is taken knowingly because the
 * alternative needs cookies, and cookies across the CloudFront/Fly origin split need
 * `SameSite=None` plus CSRF protection on every write. For a small, invite-only team on a
 * site with no third-party scripts, a bearer token is the smaller surface. Revisit it if the
 * SPA ever loads anything it did not build.
 *
 * The **user** is not trusted from storage. It is cached for a flicker-free first paint and
 * then re-fetched from `GET /api/auth/me` on load, because a role change or a deactivation
 * has to take effect on the next page load rather than the next sign-in.
 */

import { ApiProblem } from '../api/problem';
import type { TokenResponse, UserOut, UserRole } from '../api/types';

const TOKEN_KEY = 'hismaic.token';
const USER_KEY = 'hismaic.user';
const EXPIRY_KEY = 'hismaic.token_expires_at';

/**
 * Treat a token as spent this long before it actually expires.
 *
 * Without the margin a request issued at expiry−100ms arrives expired, and the annotator's
 * autosave fails with a 401 that looks like a bug rather than like a session ending.
 */
const EXPIRY_SKEW_MS = 30_000;

function read(key: string): string {
  if (typeof localStorage === 'undefined') return '';
  try {
    return localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}

function write(key: string, value: string | null): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* A full or disabled localStorage must not break the session in memory. */
  }
}

function loadUser(): UserOut | null {
  const raw = read(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UserOut;
  } catch {
    return null;
  }
}

class AuthSession {
  token = $state(read(TOKEN_KEY));
  user = $state<UserOut | null>(loadUser());
  expiresAt = $state(read(EXPIRY_KEY));

  /** True until `GET /api/auth/me` has answered once, so the shell can hold its paint. */
  checking = $state(false);

  /** Set when a session ends by itself, so the sign-in page can say why. */
  endedReason = $state<string | null>(null);

  /**
   * A token exists and has not expired by the client's clock.
   *
   * Advisory only — the server verifies the signed `exp`, and this is a *pre*check so the SPA
   * can route to sign-in instead of firing a request it knows will 401. A client clock that is
   * wrong makes this wrong, which is exactly why it is not the authority.
   */
  get hasLiveToken(): boolean {
    if (!this.token) return false;
    if (!this.expiresAt) return true;
    const expiry = Date.parse(this.expiresAt);
    if (Number.isNaN(expiry)) return true;
    return expiry - EXPIRY_SKEW_MS > Date.now();
  }

  get signedIn(): boolean {
    return this.hasLiveToken && this.user !== null;
  }

  get role(): UserRole | null {
    return this.user?.role ?? null;
  }

  /** Reads are open to every signed-in account; writing boxes is not. */
  get canWrite(): boolean {
    return this.role === 'admin' || this.role === 'annotator';
  }

  get isAdmin(): boolean {
    return this.role === 'admin';
  }

  /** The provenance actor this account's boxes are attributed to. Server-assigned. */
  get annotatorId(): string {
    return this.user?.annotator_id ?? '';
  }

  get displayName(): string {
    return this.user?.full_name || this.user?.email || this.annotatorId;
  }

  accept(payload: TokenResponse): void {
    this.token = payload.access_token;
    this.user = payload.user;
    this.expiresAt = payload.expires_at;
    this.endedReason = null;
    write(TOKEN_KEY, this.token);
    write(USER_KEY, JSON.stringify(this.user));
    write(EXPIRY_KEY, this.expiresAt);
  }

  /** Refresh the cached user from the server. Used on load, and after a role change. */
  setUser(user: UserOut): void {
    this.user = user;
    write(USER_KEY, JSON.stringify(user));
  }

  /**
   * Drop the session. `reason` is shown on the sign-in page.
   *
   * The **draft is deliberately not touched.** Signing out is not a decision to discard
   * unsaved boxes, and the draft is already refused for anyone but its owner
   * (`data-integrity.md` §4) — so it is safe where it is and it is still there when the same
   * person signs back in. Clearing it here would silently destroy work on a token expiry.
   */
  clear(reason: string | null = null): void {
    this.token = '';
    this.user = null;
    this.expiresAt = '';
    this.endedReason = reason;
    write(TOKEN_KEY, null);
    write(USER_KEY, null);
    write(EXPIRY_KEY, null);
  }

  /** The `Authorization` header, or nothing when there is no live token. */
  authHeader(): Record<string, string> {
    return this.hasLiveToken ? { Authorization: `Bearer ${this.token}` } : {};
  }
}

export const auth = new AuthSession();

/**
 * What the client does with a 401: end the session and say so.
 *
 * Kept here rather than in `client.ts` so the store owns its own lifecycle, and exported so a
 * test can drive it without a fetch.
 */
export function handleUnauthorized(problem: ApiProblem): void {
  if (!(problem instanceof ApiProblem) || problem.status !== 401) return;
  auth.clear('Your session ended. Sign in again to continue.');
}
